import { useTranslation } from "react-i18next";
import { Check, ChevronDown } from "lucide-react";
import {
  type DialogSummary,
  isFollowUpDue,
  LEAD_STATUSES,
  type LeadStatus,
  type LeadStatusFilterValue,
  useArchiveDialog,
  useUpdateDialog,
} from "@/lib/inbox";
import { formatShortDate } from "@/lib/format";
import { statusToTone, type Tone } from "@/lib/status";
import { Badge, StatusBadge } from "@/components/ui/badge";
import { SimpleSelect, type SimpleSelectOption } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { pushToast } from "@/components/Toaster";
import { cn } from "@/lib/cn";

const DOT: Record<Tone, string> = {
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger",
  info: "bg-info",
  neutral: "bg-fg-muted",
  accent: "bg-accent",
};

/** Midnight, local time, `days` from today — when a follow-up falls due. */
function dayFromNow(days: number): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + days);
  return d;
}

/** A local date as the `yyyy-mm-dd` a date input speaks. */
function toInputDate(d: Date): string {
  const two = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}`;
}

type LeadTagDialog = Pick<DialogSummary, "lead_status" | "follow_up_at">;

/**
 * The compact tag of a lead — in the dialog list and the header. The status;
 * for a follow-up with a day set, the day, and once it has come, "time to
 * write". A dialog without a status has no tag.
 */
export function LeadTag({ dialog, className }: { dialog: LeadTagDialog; className?: string }) {
  const { t } = useTranslation();
  if (!dialog.lead_status) return null;
  if (dialog.lead_status === "follow_up" && dialog.follow_up_at) {
    return isFollowUpDue(dialog) ? (
      <Badge tone="warning" variant="solid" className={className}>
        {t("inbox.follow_up.due")}
      </Badge>
    ) : (
      <Badge tone="warning" className={className}>
        {t("inbox.follow_up.on", { date: formatShortDate(dialog.follow_up_at) })}
      </Badge>
    );
  }
  return (
    <StatusBadge
      domain="lead"
      value={dialog.lead_status}
      label={t(`inbox.status.${dialog.lead_status}`)}
      className={className}
    />
  );
}

const FOLLOW_UP_IN: { key: string; days: number }[] = [
  { key: "tomorrow", days: 1 },
  { key: "in_3_days", days: 3 },
  { key: "in_week", days: 7 },
];

/**
 * What the operator decides about a lead, as menu items: the six statuses,
 * "no status", and — for a follow-up — the day to write again. One list for
 * every place it is decided: the header dropdown and the row menu of the list.
 */
export function LeadMenuItems({ dialog }: { dialog: DialogSummary }) {
  const { t } = useTranslation();
  const update = useUpdateDialog();
  const archive = useArchiveDialog();
  const check = <Check size={14} strokeWidth={2.5} aria-hidden="true" className="text-accent" />;
  const setStatus = (lead_status: LeadStatus | null) => {
    // A promise, not mutate's callbacks: this menu is gone from the screen (and
    // its callbacks with it) before the server answers.
    void update
      .mutateAsync({ dialogId: dialog.id, lead_status })
      .then(() => {
        // The conversation is over: offer to take it out of the working list.
        // Only offer — the archive does not follow from the status.
        if ((lead_status !== "closed" && lead_status !== "not_interested") || dialog.archived)
          return;
        pushToast({
          kind: "info",
          text: t("inbox.archive_offer", { status: t(`inbox.status.${lead_status}`) }),
          durationMs: 8000,
          action: {
            label: t("inbox.archive"),
            onClick: () => archive.mutate({ dialogId: dialog.id }),
          },
        });
      })
      .catch(() => {
        /* the failure is already on screen — the global mutation toast */
      });
  };
  const setDay = (day: Date | null) =>
    update.mutate({ dialogId: dialog.id, follow_up_at: day ? day.toISOString() : null });

  return (
    <>
      <DropdownMenuLabel>{t("inbox.status.label")}</DropdownMenuLabel>
      {LEAD_STATUSES.map((status) => (
        <DropdownMenuItem
          key={status}
          icon={
            <span
              aria-hidden="true"
              className={cn("h-2 w-2 rounded-full", DOT[statusToTone("lead", status)])}
            />
          }
          shortcut={dialog.lead_status === status ? check : undefined}
          onSelect={(e) => {
            // A follow-up wants a day: the menu stays open and offers them.
            if (status === "follow_up") e.preventDefault();
            setStatus(status);
          }}
        >
          {t(`inbox.status.${status}`)}
        </DropdownMenuItem>
      ))}
      <DropdownMenuItem
        icon={<span aria-hidden="true" className="h-2 w-2 rounded-full border border-fg-muted" />}
        shortcut={dialog.lead_status === null ? check : undefined}
        onSelect={() => setStatus(null)}
      >
        {t("inbox.status.none")}
      </DropdownMenuItem>

      {dialog.lead_status === "follow_up" && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuLabel>{t("inbox.follow_up.label")}</DropdownMenuLabel>
          {FOLLOW_UP_IN.map(({ key, days }) => (
            <DropdownMenuItem
              key={key}
              shortcut={formatShortDate(dayFromNow(days))}
              onSelect={() => setDay(dayFromNow(days))}
            >
              {t(`inbox.follow_up.${key}`)}
            </DropdownMenuItem>
          ))}
          <label className="flex items-center justify-between gap-3 px-2 py-1.5 text-sm">
            {t("inbox.follow_up.custom")}
            <input
              type="date"
              min={toInputDate(dayFromNow(0))}
              // Uncontrolled: a half-typed date must not be reset by a re-render.
              key={dialog.follow_up_at}
              defaultValue={dialog.follow_up_at ? toInputDate(new Date(dialog.follow_up_at)) : ""}
              onChange={(e) => e.target.value && setDay(new Date(`${e.target.value}T00:00`))}
              // The menu reads key presses as a search through its items.
              onKeyDown={(e) => e.stopPropagation()}
              className="rounded-md border border-border bg-bg-primary px-1.5 py-0.5 text-xs text-fg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            />
          </label>
          {dialog.follow_up_at && (
            <DropdownMenuItem onSelect={() => setDay(null)}>
              {t("inbox.follow_up.clear")}
            </DropdownMenuItem>
          )}
        </>
      )}
    </>
  );
}

const ALL = "__all__";
/** The list filter beyond the statuses: follow-ups whose day has come. */
export const FOLLOW_UP_DUE = "follow_up_due";
export type LeadFilterValue = LeadStatusFilterValue | typeof FOLLOW_UP_DUE;

/** The list filter: every dialog, one status, none, or "time to write". */
export function LeadStatusFilter({
  value,
  onChange,
  className,
}: {
  value: LeadFilterValue | null;
  onChange: (value: LeadFilterValue | null) => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const options: SimpleSelectOption[] = [
    { value: ALL, label: t("inbox.status.all") },
    ...LEAD_STATUSES.map((status) => ({ value: status, label: t(`inbox.status.${status}`) })),
    { value: "none", label: t("inbox.status.none") },
    { value: FOLLOW_UP_DUE, label: t("inbox.follow_up.due") },
  ];
  return (
    <SimpleSelect
      className={className}
      options={options}
      value={value ?? ALL}
      onValueChange={(v) => onChange(v === ALL ? null : (v as LeadFilterValue))}
    />
  );
}

/** The status dropdown in the header of an open dialog. */
export function LeadStatusSelect({ dialog }: { dialog: DialogSummary }) {
  const { t } = useTranslation();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t("inbox.status.label")}
          className="inline-flex shrink-0 items-center gap-1 rounded-full py-0.5 pl-0.5 pr-1.5 text-xs text-fg-muted transition-colors hover:bg-bg-elevated/60 hover:text-fg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          {dialog.lead_status ? (
            <LeadTag dialog={dialog} />
          ) : (
            <span className="pl-1.5">{t("inbox.status.label")}</span>
          )}
          <ChevronDown size={14} strokeWidth={2} aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <LeadMenuItems dialog={dialog} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
